import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { all_routes } from "../../../Router/all_routes";
import apiClient from "../../../api/apiClient";
import { API_ENDPOINTS } from "../../../api/header";
import {
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  Calculator,
  Settings2,
  PlusCircle,
  UserPlus,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { isStoreChargingTax } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { ReactSelect } from "@/components/ui/react-select";
import CustomerModal from "../../../components/modals/CustomerModal";
import ProductModal from "../../../components/modals/ProductModal";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "@/components/layout/PageContainer";

const emptyItem = () => ({
  product_id: null,
  product: null,
  is_new_item: false,
  item_name: "",
  item_description: "",
  item_sku: "",
  quantity: 1,
  unit_price: 0,
  original_price: 0,
  discount_amount: 0,
  discount_type: "fixed",
  discount_value: 0,
  tax_amount: 0,
  tax_percentage: 0,
  line_total: 0,
  notes: "",
});

const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

const calculateLineTotal = (item, storeConfig = {}) => {
  const qty = parseFloat(item.quantity || 0);
  const unitPrice = parseFloat(item.unit_price || 0);
  const basePrice = round2(qty * unitPrice);

  let discountAmt = 0;
  const dVal = parseFloat(item.discount_value || 0);
  if (item.discount_type === "percentage" && dVal > 0) {
    discountAmt = round2((basePrice * dVal) / 100);
  } else if (item.discount_type === "fixed" && dVal > 0) {
    discountAmt = round2(dVal);
  }

  const discountedPrice = round2(basePrice - discountAmt);
  const product = item.product;
  const chargesTax = isStoreChargingTax(storeConfig) && product?.is_taxable;

  let taxAmt = 0;
  const lineTotal = discountedPrice;

  if (chargesTax) {
    const taxRate = parseFloat(storeConfig.tax_percentage || 0);
    const taxMethod = product?.tax_method || "exclusive";
    if (taxMethod === "exclusive") {
      taxAmt = round2((discountedPrice * taxRate) / 100);
    }
  }

  return {
    discount_amount: round2(discountAmt),
    tax_amount: round2(taxAmt),
    line_total: round2(lineTotal),
  };
};

const NewRecurringInvoice = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const currentStore = useSelector((state) => state.current_store);
  const store_id = currentStore?.id || null;
  const userId = useSelector((state) => state.user_data)?.id || null;

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => permissions.includes(permission);

  const [recurring, setRecurring] = useState({
    customer_id: null,
    store_id: store_id || "",
    user_id: userId || "",
    frequency: "monthly",
    interval: 1,
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
    auto_send_email: false,
    notes: "",
    discount_type: "fixed",
    discount_value: 0,
  });

  const [items, setItems] = useState([emptyItem()]);
  const [productSearch, setProductSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [configModalIndex, setConfigModalIndex] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(productSearch), 400);
    return () => clearTimeout(timer);
  }, [productSearch]);

  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCustomerSearchTerm(customerSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchInput]);

  const { data: customers = [], isFetching: isCustomersFetching } = useQuery({
    queryKey: ["customers-select", customerSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.SALES.CUSTOMERS, {
        params: {
          q: customerSearchTerm || undefined,
          per_page: 15,
        },
      });
      return res.data.data || [];
    },
  });

  const { data: products = [], isFetching: productsLoading } = useQuery({
    queryKey: ["products-select", debouncedSearch, currentStore?.id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
        params: {
          perPage: 50,
          search: debouncedSearch || undefined,
          store_id: currentStore?.id || undefined,
          tracking_type: "none",
        },
      });
      return res.data.data || [];
    },
    staleTime: 60 * 1000 * 5,
    placeholderData: (prev) => prev,
  });

  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity || 0);
      const price = parseFloat(item.unit_price || 0);
      return sum + qty * price;
    }, 0);

    const itemsDiscountTotal = items.reduce(
      (sum, item) => sum + parseFloat(item.discount_amount || 0),
      0,
    );
    const itemsTaxTotal = items.reduce(
      (sum, item) => sum + parseFloat(item.tax_amount || 0),
      0,
    );
    const itemsLineTotal = items.reduce(
      (sum, item) => sum + parseFloat(item.line_total || 0),
      0,
    );

    const dVal = parseFloat(recurring.discount_value || 0);
    let headerDiscount = 0;
    if (recurring.discount_type === "percentage") {
      headerDiscount = itemsLineTotal * (dVal / 100);
    } else {
      headerDiscount = dVal;
    }

    const totalDiscount = headerDiscount;
    const total = Math.max(
      0,
      round2(itemsLineTotal + itemsTaxTotal - headerDiscount),
    );

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      tax_amount: parseFloat(itemsTaxTotal.toFixed(2)),
      item_discount_amount: parseFloat(itemsDiscountTotal.toFixed(2)),
      discount_amount: parseFloat(totalDiscount.toFixed(2)),
      total: parseFloat(total.toFixed(2)),
      item_count: items.length,
    };
  }, [items, recurring.discount_type, recurring.discount_value]);

  const updateItem = (index, field, value) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      if (field === "product_id" && value) {
        const product = products.find((p) => p.id === value);
        if (product) {
          updated[index].item_name = product.name || "";
          updated[index].item_sku = product.sku || "";
          updated[index].unit_price = parseFloat(
            product.sell_price || product.price || 0,
          );
          updated[index].original_price = parseFloat(
            product.sell_price || product.price || 0,
          );
          updated[index].product = product;
        }
      }
      if (field === "product_id" && !value) {
        updated[index].product = null;
      }

      const computed = calculateLineTotal(updated[index], currentStore);
      updated[index] = { ...updated[index], ...computed };

      return updated;
    });
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);

  const removeItem = (index) => {
    if (items.length === 1) {
      toast.warning("At least one item is required");
      return;
    }
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const res = await apiClient.post(
        API_ENDPOINTS.RECURRING_INVOICES.RECURRING_INVOICES,
        data,
      );
      return res.data;
    },
    onSuccess: () => {
      toast.success("Recurring invoice schedule created successfully");
      queryClient.invalidateQueries({ queryKey: ["recurring-invoices"] });
      navigate(all_routes.recurringInvoices);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create recurring invoice",
      );
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!recurring.store_id) {
      toast.error("Store is required");
      return;
    }

    if (!recurring.start_date) {
      toast.error("Start Date is required");
      return;
    }

    const hasInvalidItems = items.some(
      (item) =>
        !item.item_name &&
        !item.product_id &&
        parseFloat(item.quantity || 0) <= 0,
    );
    if (hasInvalidItems) {
      toast.error(
        "Please fill in item name/product and quantity for all items",
      );
      return;
    }

    const payload = {
      ...recurring,
      subtotal: totals.subtotal,
      tax_amount: totals.tax_amount,
      discount_amount: totals.discount_amount,
      total: totals.total,
      item_count: totals.item_count,
      discount_value: parseFloat(recurring.discount_value || 0),
      items: items.map((item) => ({
        product_id: item.product_id || null,
        is_new_item: item.is_new_item || false,
        item_name: item.item_name || item.product?.name || "",
        item_description: item.item_description || "",
        item_sku: item.item_sku || "",
        quantity: parseFloat(item.quantity || 1),
        unit_price: parseFloat(item.unit_price || 0),
        original_price: parseFloat(item.original_price || item.unit_price || 0),
        discount_amount: parseFloat(item.discount_amount || 0),
        discount_type: item.discount_type || "fixed",
        discount_value: parseFloat(item.discount_value || 0),
        tax_amount: parseFloat(item.tax_amount || 0),
        tax_percentage: parseFloat(item.tax_percentage || 0),
        line_total: parseFloat(item.line_total || 0),
        notes: item.notes || "",
      })),
    };

    createMutation.mutate(payload);
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="New Recurring Invoice"
          subtitle="Create a new automated invoicing schedule"
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            onClick={() => navigate(all_routes.recurringInvoices)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          {hasPermission("recurring invoice create") && (
            <Button onClick={handleSubmit} disabled={createMutation.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {createMutation.isPending ? "Saving..." : "Save"}
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Schedule Details</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Customer</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <ReactSelect
                        isClearable
                        onInputChange={(value) => setCustomerSearchInput(value)}
                        filterOption={() => true}
                        placeholder="Select customer..."
                        value={selectedCustomer}
                        onChange={(opt) => {
                          setSelectedCustomer(opt);
                          setRecurring((q) => ({
                            ...q,
                            customer_id: opt ? opt.value : null,
                          }));
                        }}
                        options={customers.map((c) => {
                          let displayName = c.full_name || c.name;
                          if (c.customer_type !== "individual" && c.company) {
                            displayName = c.company;
                          }
                          const labels = [displayName, c.phone].filter(Boolean);
                          return {
                            value: c.id,
                            label: labels.join(" - "),
                          };
                        })}
                        isLoading={isCustomersFetching}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5 flex flex-col justify-end">
                  <label className="flex items-center gap-2 cursor-pointer p-2 border rounded-md hover:bg-accent/50 transition-colors">
                    <input
                      type="checkbox"
                      checked={recurring.auto_send_email}
                      onChange={(e) =>
                        setRecurring((prev) => ({
                          ...prev,
                          auto_send_email: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm font-medium">
                      Auto-send email when generated
                    </span>
                  </label>
                </div>

                <div className="space-y-1.5">
                  <Label>Frequency</Label>
                  <Select
                    value={recurring.frequency}
                    onValueChange={(val) =>
                      setRecurring((q) => ({ ...q, frequency: val }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="capitalize">
                    Interval (Every N{" "}
                    {recurring.frequency === "daily"
                      ? "days"
                      : recurring.frequency.replace("ly", "s")}
                    )
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    value={recurring.interval}
                    onChange={(e) =>
                      setRecurring((q) => ({
                        ...q,
                        interval: parseInt(e.target.value) || 1,
                      }))
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={recurring.start_date}
                    onChange={(e) =>
                      setRecurring((q) => ({
                        ...q,
                        start_date: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>End Date (Optional)</Label>
                  <Input
                    type="date"
                    value={recurring.end_date}
                    onChange={(e) =>
                      setRecurring((q) => ({
                        ...q,
                        end_date: e.target.value,
                      }))
                    }
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Line Items</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="px-5">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8">#</TableHead>
                        <TableHead className="min-w-[220px]">Item</TableHead>
                        <TableHead className="min-w-[80px]">Qty</TableHead>
                        <TableHead className="min-w-[110px]">
                          Unit Price
                        </TableHead>
                        <TableHead className="min-w-[110px] text-right">
                          Line Total
                        </TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item, index) => (
                        <TableRow key={index} className="group">
                          <TableCell className="text-muted-foreground text-sm align-top pt-3">
                            {index + 1}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5 min-w-[200px]">
                              <div className="flex-1">
                                <ReactSelect
                                  isLoading={productsLoading}
                                  onInputChange={(val) => setProductSearch(val)}
                                  filterOption={() => true}
                                  isClearable
                                  placeholder="Select product..."
                                  value={
                                    item.product_id
                                      ? {
                                        value: item.product_id,
                                        label:
                                          item.product?.name ||
                                          products.find(
                                            (p) => p.id === item.product_id,
                                          )?.name ||
                                          "Product",
                                      }
                                      : null
                                  }
                                  onChange={(opt) =>
                                    updateItem(
                                      index,
                                      "product_id",
                                      opt ? opt.value : null,
                                    )
                                  }
                                  options={products.map((p) => ({
                                    value: p.id,
                                    label: p.name,
                                  }))}
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="align-top pt-3">
                            <Input
                              type="number"
                              min="1"
                              step="1"
                              value={item.quantity}
                              onChange={(e) =>
                                updateItem(index, "quantity", e.target.value)
                              }
                              className="h-8 w-20"
                            />
                          </TableCell>
                          <TableCell className="align-top pt-3">
                            <div className="flex items-center gap-1">
                              <span className="text-muted-foreground text-xs">
                                {currencySymbol}
                              </span>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) =>
                                  updateItem(
                                    index,
                                    "unit_price",
                                    e.target.value,
                                  )
                                }
                                className="h-8 w-24"
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-right align-top pt-3">
                            <div className="font-medium">
                              {currencySymbol}
                              {parseFloat(item.line_total || 0).toFixed(2)}
                            </div>
                            {(parseFloat(item.discount_value || 0) > 0 ||
                              parseFloat(item.tax_amount || 0) > 0) && (
                                <div className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                                  {parseFloat(item.discount_value || 0) > 0 && (
                                    <div>
                                      Disc:{" "}
                                      {item.discount_type === "percentage"
                                        ? `${parseFloat(item.discount_value).toFixed(2)}%`
                                        : `${currencySymbol}${parseFloat(item.discount_amount || 0).toFixed(2)}`}
                                    </div>
                                  )}
                                  {parseFloat(item.tax_amount || 0) > 0 && (
                                    <div>
                                      Tax (
                                      {item.product?.tax_method === "inclusive"
                                        ? "incl"
                                        : `${parseFloat(currentStore?.tax_percentage || 0).toFixed(0)}%`}
                                      ): {currencySymbol}
                                      {parseFloat(item.tax_amount).toFixed(2)}
                                    </div>
                                  )}
                                </div>
                              )}
                          </TableCell>
                          <TableCell className="align-top pt-2">
                            {hasPermission("recurring invoice item update") && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                title="Configure item (discount, tax, notes)"
                                onClick={() => setConfigModalIndex(index)}
                              >
                                <Settings2 className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                          <TableCell className="align-top pt-2">
                            {hasPermission("recurring invoice item delete") && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-foreground"
                                onClick={() => removeItem(index)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
              <CardFooter className="border-t pt-3">
                {hasPermission("recurring invoice item create") && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={addItem}
                    className="text-muted-foreground"
                  >
                    <Plus className="mr-1.5 h-4 w-4" />
                    Add another line
                  </Button>
                )}
              </CardFooter>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Additional notes for generated invoices..."
                  value={recurring.notes}
                  onChange={(e) =>
                    setRecurring((q) => ({ ...q, notes: e.target.value }))
                  }
                  rows={4}
                />
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Calculator className="h-4 w-4 text-muted-foreground" />
                  <CardTitle>Summary</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>
                    {currencySymbol}
                    {totals.subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Item Discounts</span>
                  <span className="text-destructive">
                    -{currencySymbol}
                    {(totals.item_discount_amount || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span>
                    {currencySymbol}
                    {totals.tax_amount.toFixed(2)}
                  </span>
                </div>

                <Separator />
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Additional Discount
                  </Label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={recurring.discount_type}
                      onValueChange={(val) =>
                        setRecurring((q) => ({ ...q, discount_type: val }))
                      }
                    >
                      <SelectTrigger className="h-8 w-28 shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed">
                          {currencySymbol} Fixed
                        </SelectItem>
                        <SelectItem value="percentage">% Percent</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      className="h-8"
                      value={recurring.discount_value}
                      onChange={(e) =>
                        setRecurring((q) => ({
                          ...q,
                          discount_value: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <Separator />
                <div className="flex items-center justify-between font-semibold">
                  <span>Total</span>
                  <span className="text-lg">
                    {currencySymbol}
                    {totals.total.toFixed(2)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground text-right">
                  {totals.item_count} item{totals.item_count !== 1 ? "s" : ""}
                </div>
              </CardContent>
            </Card>
            <div className="text-xs text-muted-foreground bg-accent/50 p-3 rounded-md border border-border/50">
              <Info className="h-4 w-4 inline mr-1.5 align-text-bottom" />
              Only products with no tracking type can be used in recurring
              invoices.
            </div>
          </div>
        </div>
      </form>

      {/* Per-item configuration modal */}
      {configModalIndex !== null && items[configModalIndex] && (
        <Dialog
          open={configModalIndex !== null}
          onOpenChange={() => setConfigModalIndex(null)}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Configure Item</DialogTitle>
              <DialogDescription>
                {items[configModalIndex]?.item_name ||
                  `Item ${configModalIndex + 1}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* SKU */}
              <div className="space-y-1.5">
                <Label>SKU</Label>
                <Input
                  placeholder="Item SKU..."
                  value={items[configModalIndex]?.item_sku || ""}
                  onChange={(e) =>
                    updateItem(configModalIndex, "item_sku", e.target.value)
                  }
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea
                  placeholder="Item description..."
                  value={items[configModalIndex]?.item_description || ""}
                  onChange={(e) =>
                    updateItem(
                      configModalIndex,
                      "item_description",
                      e.target.value,
                    )
                  }
                  rows={2}
                />
              </div>

              {/* Discount */}
              <div className="space-y-1.5">
                <Label>Discount</Label>
                <div className="flex gap-2">
                  <Select
                    value={items[configModalIndex]?.discount_type}
                    onValueChange={(val) =>
                      updateItem(configModalIndex, "discount_type", val)
                    }
                  >
                    <SelectTrigger className="w-32 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">
                        {currencySymbol} Fixed
                      </SelectItem>
                      <SelectItem value="percentage">% Percent</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={items[configModalIndex]?.discount_value || 0}
                    onChange={(e) =>
                      updateItem(
                        configModalIndex,
                        "discount_value",
                        e.target.value,
                      )
                    }
                  />
                </div>
                {parseFloat(items[configModalIndex]?.discount_amount || 0) >
                  0 && (
                    <p className="text-xs text-muted-foreground">
                      Discount amount: {currencySymbol}
                      {parseFloat(
                        items[configModalIndex]?.discount_amount || 0,
                      ).toFixed(2)}
                    </p>
                  )}
              </div>

              {/* Tax - informational, auto-applied from store + product settings */}
              <div className="space-y-1.5">
                <Label>Tax</Label>
                {(() => {
                  const item = items[configModalIndex];
                  const product = item?.product;
                  const storeCharges = isStoreChargingTax(currentStore);
                  const isProductTaxable = product?.is_taxable;
                  const taxMethod = product?.tax_method || "exclusive";
                  const taxRate = parseFloat(currentStore?.tax_percentage || 0);
                  const taxAmt = parseFloat(item?.tax_amount || 0);
                  if (!product) {
                    return (
                      <p className="text-xs text-muted-foreground">
                        No tax - custom item, not linked to a product.
                      </p>
                    );
                  }
                  if (!storeCharges) {
                    return (
                      <p className="text-xs text-muted-foreground">
                        No tax - store does not charge sales tax.
                      </p>
                    );
                  }
                  if (!isProductTaxable) {
                    return (
                      <p className="text-xs text-muted-foreground">
                        No tax - product is not taxable.
                      </p>
                    );
                  }
                  if (taxMethod === "inclusive") {
                    return (
                      <p className="text-xs text-muted-foreground">
                        Tax inclusive ({taxRate}%) - price already includes tax.
                      </p>
                    );
                  }
                  return (
                    <p className="text-xs text-muted-foreground">
                      Tax exclusive - {taxRate}% applied ={" "}
                      <span className="font-medium text-foreground">
                        {currencySymbol}
                        {taxAmt.toFixed(2)}
                      </span>
                    </p>
                  );
                })()}
              </div>

              {/* Notes */}
              <div className="space-y-1.5">
                <Label>Item Notes</Label>
                <Textarea
                  placeholder="Notes for this item..."
                  value={items[configModalIndex]?.notes || ""}
                  onChange={(e) =>
                    updateItem(configModalIndex, "notes", e.target.value)
                  }
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setConfigModalIndex(null)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </PageContainer>
  );
};

export default NewRecurringInvoice;
